import React, { createContext } from 'react';

import { ValidationError } from 'yup';

import { AttachmentPreview } from 'lane-shared/types/attachment';

import { ThemeType } from '../types/Theme';
import { UserContentInteractionType } from '../types/UserContentInteraction';
import { ContentType } from '../types/content/Content';
import { SearchOptions } from '../types/filters/SearchOptions';
import { ReservableAvailabilityRange } from '../hooks/useReservableAvailabilityByRange';

export type ContentRendererBlockContextType = {
  sectionSearch?: SearchOptions;
  isVerificationEmailSent?: boolean;
  // pushed up by useReservableAvailability so ContentRenderer's submit-time
  // capacity check (M-03) can read real per-slot remaining capacity.
  reservableAvailabilityRanges?: ReservableAvailabilityRange[];
  // fallback capacity for slots with no bookings yet (sparse-interval API
  // returns no entry for them) — without this, getSlotAvailability can't
  // tell a fresh slot's real capacity, and the submit-time guard silently
  // never blocks it.
  maxQuantityPerSlot?: number;
};

export type ContentRendererContextType = {
  scrollRef: React.Ref<React.ReactNode>;
  loading: boolean;
  isRefreshing: boolean;
  disabled: boolean;
  submitAttempted: boolean;
  editMode: boolean;
  // True when the member is editing an existing reservation from the
  // Receipt's ✎ Edit action — NOT the same as `editMode`. `editMode` means
  // "the admin content-builder canvas is editing block layout/structure":
  // per createBlockRenderer.tsx it turns OFF block data subscriptions,
  // links, and validation, and turns ON the block-editing overlay UI.
  // Reusing it here would silently break the reservation-edit form (no
  // subscriptions means no pre-fill/guest-invite/date-picker interaction,
  // and no validation means capacity/required-field checks stop running).
  // Lets blocks (e.g. the submit ButtonBlock) render differently for this
  // case without duplicating the whole block tree. Optional because only
  // mobile's ContentRenderer sets it today — web is building its own
  // parallel Updating feature (see Updating Feature Web PRD) and will
  // likely want to set this too once that lands; it just hasn't yet, so
  // no lane-web change is needed for this specific mobile-only PR.
  isEditingInteraction?: boolean;
  // True when isEditingInteraction is true and the member hasn't changed
  // any field yet — lets the submit ButtonBlock disable "Update" so an
  // untouched edit can't fire a no-op mutation. Mobile-only, same reasoning
  // as isEditingInteraction above.
  isEditingInteractionUnchanged?: boolean;
  // The reservation being edited, exactly as it was before this edit
  // session started (unlike `interaction`, which is the live draft and
  // changes as the member edits). Blocks need this fixed original value —
  // e.g. ReservableInput's overlap check must exclude the member's own
  // pre-existing slot from the "unavailable" set, or picking any slot
  // (even the one they already have) looks like a conflicting double
  // booking. Mobile-only, same reasoning as isEditingInteraction above.
  editingInteractionRecord?: UserContentInteractionType | null;
  previewLanguage?: string;
  dataValidation: ValidationError;
  content: ContentType;
  interaction: UserContentInteractionType;
  blockContext: ContentRendererBlockContextType;
  updateBlockContext: (
    update: Partial<ContentRendererBlockContextType>
  ) => void;
  theme: ThemeType;
  onSubmit: () => Promise<void>;
  onLink: (link: any) => Promise<void>;
  onClick: (e: any) => void;
  onInteractionUpdated: (update: Partial<UserContentInteractionType>) => void;
  preventSubmission: boolean;
  hideSubmissionBlocks: boolean;
  submissionCompletedAt?: Date;
  submitButtonLabel?: string;
  draftAttachmentsCreate?: AttachmentPreview[];
  updateAttachmentsUCICreate?: (data: AttachmentPreview[]) => {};
};

const ContentRendererContext = createContext({} as ContentRendererContextType);

export default ContentRendererContext;
